import { generateAutomaticCover } from './projectCoverAutoCrop';
import { PROJECT_CARD_ASPECT_RATIO } from './projectCoverAspect';

/**
 * Format a duration in seconds as `m:ss` (e.g. 15 → `0:15`, 90 → `1:30`).
 *
 * @param {number} totalSeconds
 * @returns {string}
 */
export function formatMediaTimestamp(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${String(rem).padStart(2, '0')}`;
}

/**
 * Load metadata + seek a video File/Blob to a fractional position.
 *
 * @param {File|Blob} source
 * @param {number} position 0–1 through the video (0.5 = mid-video)
 * @returns {Promise<{
 *   video: HTMLVideoElement,
 *   objectUrl: string,
 *   durationSeconds: number,
 *   frameSeconds: number,
 * }>}
 */
const VIDEO_LOAD_TIMEOUT_MS = 15000;

function loadVideoAtPosition(source, position) {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined' || !source) {
      reject(new Error('Video frame extraction is unavailable'));
      return;
    }

    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    const objectUrl = URL.createObjectURL(source);
    let settled = false;
    let timeoutId = null;

    const cleanupListeners = () => {
      if (timeoutId != null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      video.onloadedmetadata = null;
      video.onseeked = null;
      video.onerror = null;
    };

    const fail = (err) => {
      if (settled) return;
      settled = true;
      cleanupListeners();
      URL.revokeObjectURL(objectUrl);
      reject(err instanceof Error ? err : new Error('Could not read video'));
    };

    timeoutId = setTimeout(() => {
      fail(
        new Error(
          'Timed out waiting for video metadata or seek while extracting a frame'
        )
      );
    }, VIDEO_LOAD_TIMEOUT_MS);

    video.onerror = () => fail(new Error('Could not load video for thumbnail'));

    video.onloadedmetadata = () => {
      const duration = Number(video.duration);
      if (!Number.isFinite(duration) || duration <= 0) {
        fail(new Error('Video has no duration'));
        return;
      }

      const frameSeconds = Math.floor(
        Math.max(0, Math.min(1, position)) * duration
      );
      // Seeking to exactly duration can fail in some browsers — nudge back slightly.
      const seekTo = Math.min(frameSeconds, Math.max(0, duration - 0.05));

      video.onseeked = () => {
        if (settled) return;
        settled = true;
        cleanupListeners();
        resolve({
          video,
          objectUrl,
          durationSeconds: duration,
          frameSeconds,
        });
      };

      try {
        video.currentTime = seekTo;
      } catch (err) {
        fail(err);
      }
    };

    video.src = objectUrl;
    video.load();
  });
}

/**
 * Capture the video's current frame as a JPEG Blob.
 *
 * @param {HTMLVideoElement} video
 * @returns {Promise<Blob|null>}
 */
function captureVideoFrameBlob(video) {
  return new Promise((resolve) => {
    if (
      typeof document === 'undefined' ||
      !video ||
      !video.videoWidth ||
      !video.videoHeight
    ) {
      resolve(null);
      return;
    }

    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => resolve(blob || null), 'image/jpeg', 0.92);
    } catch (err) {
      resolve(null);
    }
  });
}

/**
 * Extract the mid-video (50%) frame, then run ticket-2 automatic cover crop.
 *
 * @param {File|Blob} source
 * @param {number} [aspectRatio=PROJECT_CARD_ASPECT_RATIO]
 * @returns {Promise<{
 *   normalised: { x: number, y: number, w: number, h: number },
 *   pixel: { x: number, y: number, width: number, height: number },
 *   previewUrl: string|null,
 *   naturalWidth: number,
 *   naturalHeight: number,
 *   durationSeconds: number,
 *   frameSeconds: number,
 * }|null>}
 */
export async function generateAutomaticVideoCover(
  source,
  aspectRatio = PROJECT_CARD_ASPECT_RATIO
) {
  const loaded = await loadVideoAtPosition(source, 0.5);
  const { video, objectUrl, durationSeconds, frameSeconds } = loaded;

  try {
    const frameBlob = await captureVideoFrameBlob(video);
    if (!frameBlob) {
      return null;
    }

    const cover = await generateAutomaticCover(frameBlob, aspectRatio);
    if (!cover) {
      return null;
    }

    return {
      ...cover,
      durationSeconds,
      frameSeconds,
    };
  } finally {
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(objectUrl);
  }
}
